import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatTableDataSource } from '@angular/material/table';
import { SignService } from 'src/app/signs/sign.service';
import {
  ToasterService,
  ToastStatus,
} from 'src/app/core/toaster/toaster.service';
import * as XLSX from 'xlsx';

export interface AdminBoardActivitySummaryDto {
  agentName: string;
  agentUsername: string;
  change: number;
  collect: number;
  erect: number;
  reerect: number;
  stray: number;
  total: number;
}

@Component({
  selector: 'app-board-action-summary',
  templateUrl: './board-action-summary.component.html',
  styleUrls: ['./board-action-summary.component.scss'],
})
export class BoardActionSummaryComponent implements OnInit {
  displayedColumns: string[] = ['agent', 'C', 'COL', 'E', 'R', 'STR', 'total'];
  dataSource: MatTableDataSource<AdminBoardActivitySummaryDto> =
    new MatTableDataSource();
  isLoading = false;
  date = new FormControl(new Date());
  footerData: AdminBoardActivitySummaryDto = {
    agentName: 'Grand Total',
    agentUsername: '',
    change: 0,
    collect: 0,
    erect: 0,
    reerect: 0,
    stray: 0,
    total: 0,
  };

  constructor(
    private signService: SignService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.fetchSummary();
  }

  fetchSummary(): void {
    const ctrlValue = this.date.value;
    if (!ctrlValue) return;

    const month = ctrlValue.getMonth() + 1;
    const year = ctrlValue.getFullYear();

    this.isLoading = true;
    this.signService.fetchAdminBoardActionSummary(month, year).subscribe({
      next: data => {
        this.dataSource.data = data;
        this.calculateFooter(data);
        this.isLoading = false;
      },
      error: err => {
        console.error(err);
        this.toasterService.toast(ToastStatus.error, 'Failed to load summary');
        this.isLoading = false;
        this.dataSource.data = [];
      },
    });
  }

  calculateFooter(data: AdminBoardActivitySummaryDto[]) {
    this.footerData = data.reduce(
      (acc, curr) => {
        acc.change += curr.change;
        acc.collect += curr.collect;
        acc.erect += curr.erect;
        acc.reerect += curr.reerect;
        acc.stray += curr.stray;
        acc.total += curr.total;
        return acc;
      },
      {
        agentName: 'Grand Total',
        agentUsername: '',
        change: 0,
        collect: 0,
        erect: 0,
        reerect: 0,
        stray: 0,
        total: 0,
      }
    );
  }

  chosenYearHandler(normalizedYear: Date) {
    const ctrlValue = this.date.value!;
    ctrlValue.setFullYear(normalizedYear.getFullYear());
    this.date.setValue(ctrlValue);
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    const ctrlValue = this.date.value!;
    ctrlValue.setMonth(normalizedMonth.getMonth());
    this.date.setValue(ctrlValue);
    datepicker.close();
    this.fetchSummary();
  }

  exportToExcel() {
    const dataToExport = this.dataSource.data.map(item => ({
      'Row Labels': item.agentUsername, // Using Username as Row Label based on request
      C: item.change,
      COL: item.collect,
      E: item.erect,
      R: item.reerect,
      STR: item.stray,
      'Grand Total': item.total,
    }));

    // Add footer row
    dataToExport.push({
      'Row Labels': 'Grand Total',
      C: this.footerData.change,
      COL: this.footerData.collect,
      E: this.footerData.erect,
      R: this.footerData.reerect,
      STR: this.footerData.stray,
      'Grand Total': this.footerData.total,
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');

    const ctrlValue = this.date.value!;
    const monthStr = ctrlValue.toLocaleString('default', { month: 'short' });
    const yearStr = ctrlValue.getFullYear();
    const filename = `Board_Actions_Summary_${monthStr}_${yearStr}.xlsx`;

    XLSX.writeFile(wb, filename);
  }
}
